/**
 * Tiny typed fetch wrapper. The shape of every request/response matches the
 * Pydantic schemas defined in `controller/controller/api/schemas.py`.
 */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message?: string) {
    super(message ?? extractDetail(body) ?? `HTTP ${status}`);
  }
}

/**
 * FastAPI surfaces operator-readable errors as `{ "detail": "..." }`.
 * Pull that out so `err.message` is "another rollout is already in
 * progress; wait for it to finish" instead of a bare "HTTP 409" — every
 * call site that does `window.alert(err.message)` or `setError(err.message)`
 * gets the real reason for free.
 *
 * `detail` can also be a list of validation errors (422). We don't try
 * to format those here; callers that care about field-level errors
 * already inspect `err.body` directly.
 */
function extractDetail(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === "string" && detail.length > 0) return detail;
  return undefined;
}

interface RequestOpts {
  /** Extra request headers (merged on top of Content-Type / Accept). */
  headers?: Record<string, string>;
}

async function request<T>(
  path: string,
  init?: RequestInit,
  opts?: RequestOpts,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    // Always send the session cookie; the controller's auth dependency
    // looks it up to resolve the current user.
    credentials: "include",
    // Spread `init` first, then set the merged headers last — otherwise
    // a caller passing `init.headers` would clobber the whole object and
    // silently drop Content-Type / Accept / If-Match.
    ...init,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(init?.headers ?? {}),
      ...(opts?.headers ?? {}),
    },
  });
  if (!res.ok) {
    // A `Response` body is a one-shot stream. If we call `res.json()`
    // first and it fails (empty body, HTML error page, etc.) the
    // subsequent `res.text()` blows up with "body stream already read",
    // masking the real server status. Read the bytes once as text and
    // *then* try to upgrade to JSON.
    const raw = await res.text();
    let body: unknown = raw;
    if (raw.length > 0) {
      try {
        body = JSON.parse(raw);
      } catch {
        // not JSON — keep the raw string as the body
      }
    }
    // 401 is a normal "not logged in" signal; let callers (typically the
    // AuthProvider) handle it. Other codes are real errors.
    throw new ApiError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Plain-text variant of {@link request}. Some controller endpoints
 * (e.g. ``GET /pools/{id}/skyserve-logs``) return a multi-line dump
 * via FastAPI's ``PlainTextResponse`` because the payload is meant to
 * be shown verbatim in a ``<pre>`` and JSON-encoding it (with its
 * mandatory escaping of newlines and quotes) just adds noise.
 */
async function requestText(
  path: string,
  init?: RequestInit,
  opts?: RequestOpts,
): Promise<string> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    // Headers merged last so `init.headers` can't clobber Accept. See
    // the note in `request` above.
    ...init,
    headers: {
      Accept: "text/plain",
      ...(init?.headers ?? {}),
      ...(opts?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const raw = await res.text();
    let body: unknown = raw;
    if (raw.length > 0) {
      try {
        body = JSON.parse(raw);
      } catch {
        // not JSON — keep the raw string as the body
      }
    }
    throw new ApiError(res.status, body);
  }
  return await res.text();
}

export const api = {
  get: <T>(path: string, opts?: RequestOpts) => request<T>(path, undefined, opts),
  getText: (path: string, opts?: RequestOpts) => requestText(path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>(
      path,
      { method: "POST", body: body !== undefined ? JSON.stringify(body) : null },
      opts,
    ),
  put: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>(
      path,
      { method: "PUT", body: body !== undefined ? JSON.stringify(body) : null },
      opts,
    ),
  patch: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>(
      path,
      { method: "PATCH", body: body !== undefined ? JSON.stringify(body) : null },
      opts,
    ),
  delete: <T>(path: string, opts?: RequestOpts) =>
    request<T>(path, { method: "DELETE" }, opts),
};
